import type {PubSubServiceV1, PubSubTopicV1} from '@smart/pub-sub-service';
import type {ButtonProps} from '@smart/react-components';
import {Button, IconButton, Theme} from '@smart/react-components';
import classnames from 'classnames';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {useOutsideClick} from '../../hooks/use-outside-click';
import {useAnalytics} from '../../utils/analytics';
import styles from './popup-container.module.scss';

export type PopupContainerProps = {
  children: React.ReactNode;
  subscribeFunc: PubSubServiceV1['subscribe'];
  topic: PubSubTopicV1;
  closeButtonStyles?: ButtonProps['className'];
  className?: string;
  theme?: 'dark' | 'light';
  autoShowPopup?: boolean;
};

export type PopupContainerMethods = {
  close: () => void;
};

export const PopupContainer = forwardRef<
  PopupContainerMethods,
  PopupContainerProps
>(
  (
    {
      children,
      subscribeFunc,
      topic,
      closeButtonStyles,
      className,
      theme,
      autoShowPopup = false,
    },
    forwardedRef,
  ) => {
    const ref = useRef<HTMLDivElement>(null);

    useImperativeHandle(forwardedRef, () => ({
      close: () => setShowPopup(false),
    }));

    const [showPopup, setShowPopup] = useState(autoShowPopup);
    const unsubscribe = useRef<ReturnType<typeof subscribeFunc> | null>(null);
    const handleClose = () => {
      setShowPopup(false);
    };
    useOutsideClick(ref, handleClose);
    const trackEvent = useAnalytics();

    useEffect(() => {
      setShowPopup(autoShowPopup);
    }, [autoShowPopup]);

    useEffect(() => {
      if (showPopup) {
        document.body.classList.add(`no-scroll`);
      } else {
        document.body.classList.remove(`no-scroll`);
      }
      unsubscribe.current = subscribeFunc(topic, () => {
        if (topic === `show-newsletter-popup`) {
          trackEvent(`success`, `newsletter-form-overlay`);
        }
        if (topic === `show-campaign-popup`) {
          trackEvent(`success`, `testdrive-overlay`);
        }
        setShowPopup(true);
      });
      return () => {
        document.body.classList.remove(`no-scroll`);
        if (unsubscribe.current) unsubscribe.current();
      };
    }, [showPopup, topic, subscribeFunc]);

    return (
      <Theme theme={theme || `light`}>
        <div
          className={classnames(styles.overlay, {
            [styles.visible as string]: showPopup,
          })}
        />
        <div
          className={classnames(
            styles.popup,
            {
              [styles.visible as string]: showPopup,
            },
            className,
          )}
          ref={ref}
        >
          <IconButton
            className={closeButtonStyles ?? styles.overlay__popupCloseButton}
            onClick={handleClose}
            variant="ghost"
            mode={300}
          >
            <Button.Icon aria-label="close" icon="close-without-circle" />
          </IconButton>
          {children}
        </div>
      </Theme>
    );
  },
);
